const ERROR_NOT_LOADED = new Error("ffmpeg is not loaded, call `await ffmpeg.load()` first");
const ERROR_TERMINATED = new Error("called FFmpeg.terminate()");
const ERROR_CLASS_WORKER_URL_REQUIRED = new Error("classWorkerURL is required for the vendored FFmpeg host");

enum FFMessageType {
  LOAD = "LOAD",
  EXEC = "EXEC",
  FFPROBE = "FFPROBE",
  WRITE_FILE = "WRITE_FILE",
  READ_FILE = "READ_FILE",
  DELETE_FILE = "DELETE_FILE",
  RENAME = "RENAME",
  CREATE_DIR = "CREATE_DIR",
  LIST_DIR = "LIST_DIR",
  DELETE_DIR = "DELETE_DIR",
  ERROR = "ERROR",
  PROGRESS = "PROGRESS",
  LOG = "LOG",
  MOUNT = "MOUNT",
  UNMOUNT = "UNMOUNT",
}

type LogEvent = {
  message: string;
  type?: string;
};

type ProgressEvent = {
  progress: number;
  time?: number;
};

type FFmpegEventMap = {
  log: LogEvent;
  progress: ProgressEvent;
};

type Callback<T> = (event: T) => void;

type FFMessageOptions = {
  signal?: AbortSignal;
};

type FFmpegLoadConfig = {
  classWorkerURL?: string;
  coreURL?: string;
  wasmURL?: string;
  workerURL?: string;
  coreData?: Uint8Array;
  wasmBinary?: Uint8Array;
};

let messageID = 0;

function getMessageID(): number {
  const currentMessageID = messageID;
  messageID += 1;
  return currentMessageID;
}

export enum FFFSType {
  MEMFS = "MEMFS",
  NODEFS = "NODEFS",
  NODERAWFS = "NODERAWFS",
  IDBFS = "IDBFS",
  WORKERFS = "WORKERFS",
  PROXYFS = "PROXYFS",
}

export class FFmpeg {
  #worker: Worker | null = null;
  #resolves: Record<number, (value: unknown) => void> = {};
  #rejects: Record<number, (reason?: unknown) => void> = {};
  #logEventCallbacks: Array<Callback<LogEvent>> = [];
  #progressEventCallbacks: Array<Callback<ProgressEvent>> = [];

  loaded = false;

  #registerHandlers = () => {
    if (!this.#worker) {
      return;
    }

    this.#worker.onmessage = ({ data: { id, type, data } }) => {
      switch (type as FFMessageType) {
        case FFMessageType.LOAD:
          this.loaded = true;
          this.#resolves[id]?.(data);
          break;
        case FFMessageType.MOUNT:
        case FFMessageType.UNMOUNT:
        case FFMessageType.EXEC:
        case FFMessageType.FFPROBE:
        case FFMessageType.WRITE_FILE:
        case FFMessageType.READ_FILE:
        case FFMessageType.DELETE_FILE:
        case FFMessageType.RENAME:
        case FFMessageType.CREATE_DIR:
        case FFMessageType.LIST_DIR:
        case FFMessageType.DELETE_DIR:
          this.#resolves[id]?.(data);
          break;
        case FFMessageType.LOG:
          this.#logEventCallbacks.forEach((callback) => callback(data));
          break;
        case FFMessageType.PROGRESS:
          this.#progressEventCallbacks.forEach((callback) => callback(data));
          break;
        case FFMessageType.ERROR:
          this.#rejects[id]?.(data);
          break;
      }

      delete this.#resolves[id];
      delete this.#rejects[id];
    };
  };

  #send = <T>(
    { type, data }: { type: FFMessageType; data: unknown },
    transferables: Transferable[] = [],
    signal?: AbortSignal
  ): Promise<T> => {
    if (!this.#worker) {
      return Promise.reject(ERROR_NOT_LOADED);
    }

    return new Promise((resolve, reject) => {
      const id = getMessageID();

      this.#worker?.postMessage({ id, type, data }, transferables);
      this.#resolves[id] = resolve;
      this.#rejects[id] = reject;

      signal?.addEventListener(
        "abort",
        () => {
          reject(new DOMException(`Message # ${id} was aborted`, "AbortError"));
        },
        { once: true }
      );
    });
  };

  on<EventName extends keyof FFmpegEventMap>(
    event: EventName,
    callback: Callback<FFmpegEventMap[EventName]>
  ): void {
    if (event === "log") {
      this.#logEventCallbacks.push(callback as Callback<LogEvent>);
      return;
    }

    this.#progressEventCallbacks.push(callback as Callback<ProgressEvent>);
  }

  off<EventName extends keyof FFmpegEventMap>(
    event: EventName,
    callback: Callback<FFmpegEventMap[EventName]>
  ): void {
    if (event === "log") {
      this.#logEventCallbacks = this.#logEventCallbacks.filter(
        (storedCallback) => storedCallback !== callback
      );
      return;
    }

    this.#progressEventCallbacks = this.#progressEventCallbacks.filter(
      (storedCallback) => storedCallback !== callback
    );
  }

  load = (
    { classWorkerURL, ...config }: FFmpegLoadConfig = {},
    { signal }: FFMessageOptions = {}
  ): Promise<boolean> => {
    if (!this.#worker) {
      if (!classWorkerURL) {
        return Promise.reject(ERROR_CLASS_WORKER_URL_REQUIRED);
      }

      this.#worker = new Worker(new URL(classWorkerURL, import.meta.url), { type: "module" });

      this.#registerHandlers();
    }

    const transferables: Transferable[] = [];

    if (config.coreData instanceof Uint8Array) {
      transferables.push(config.coreData.buffer);
    }

    if (config.wasmBinary instanceof Uint8Array) {
      transferables.push(config.wasmBinary.buffer);
    }

    return this.#send<boolean>(
      {
        type: FFMessageType.LOAD,
        data: config,
      },
      transferables,
      signal
    );
  };

  exec = (args: string[], timeout = -1, { signal }: FFMessageOptions = {}): Promise<number> =>
    this.#send<number>(
      {
        type: FFMessageType.EXEC,
        data: { args, timeout },
      },
      [],
      signal
    );

  ffprobe = (args: string[], timeout = -1, { signal }: FFMessageOptions = {}): Promise<number> =>
    this.#send<number>(
      {
        type: FFMessageType.FFPROBE,
        data: { args, timeout },
      },
      [],
      signal
    );

  terminate = (): void => {
    for (const id of Object.keys(this.#rejects)) {
      this.#rejects[Number(id)]?.(ERROR_TERMINATED);
      delete this.#rejects[Number(id)];
      delete this.#resolves[Number(id)];
    }

    if (this.#worker) {
      this.#worker.terminate();
      this.#worker = null;
      this.loaded = false;
    }
  };

  writeFile = (
    path: string,
    data: Uint8Array | string,
    { signal }: FFMessageOptions = {}
  ): Promise<boolean> => {
    const transferables: Transferable[] = [];

    if (data instanceof Uint8Array) {
      transferables.push(data.buffer);
    }

    return this.#send<boolean>(
      {
        type: FFMessageType.WRITE_FILE,
        data: { path, data },
      },
      transferables,
      signal
    );
  };

  mount = (fsType: FFFSType, options: unknown, mountPoint: string): Promise<boolean> =>
    this.#send<boolean>({
      type: FFMessageType.MOUNT,
      data: { fsType, options, mountPoint },
    });

  unmount = (mountPoint: string): Promise<boolean> =>
    this.#send<boolean>({
      type: FFMessageType.UNMOUNT,
      data: { mountPoint },
    });

  readFile = (
    path: string,
    encoding: "utf8" | "binary" = "binary",
    { signal }: FFMessageOptions = {}
  ): Promise<Uint8Array | string> =>
    this.#send<Uint8Array | string>(
      {
        type: FFMessageType.READ_FILE,
        data: { path, encoding },
      },
      [],
      signal
    );

  deleteFile = (path: string, { signal }: FFMessageOptions = {}): Promise<boolean> =>
    this.#send<boolean>(
      {
        type: FFMessageType.DELETE_FILE,
        data: { path },
      },
      [],
      signal
    );

  rename = (oldPath: string, newPath: string, { signal }: FFMessageOptions = {}): Promise<boolean> =>
    this.#send<boolean>(
      {
        type: FFMessageType.RENAME,
        data: { oldPath, newPath },
      },
      [],
      signal
    );

  createDir = (path: string, { signal }: FFMessageOptions = {}): Promise<boolean> =>
    this.#send<boolean>(
      {
        type: FFMessageType.CREATE_DIR,
        data: { path },
      },
      [],
      signal
    );

  listDir = (
    path: string,
    { signal }: FFMessageOptions = {}
  ): Promise<Array<{ isDir: boolean; name: string }>> =>
    this.#send<Array<{ isDir: boolean; name: string }>>(
      {
        type: FFMessageType.LIST_DIR,
        data: { path },
      },
      [],
      signal
    );

  deleteDir = (path: string, { signal }: FFMessageOptions = {}): Promise<boolean> =>
    this.#send<boolean>(
      {
        type: FFMessageType.DELETE_DIR,
        data: { path },
      },
      [],
      signal
    );
}
